import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Info, X } from "lucide-react";
import { slurpGroups } from "../../lib/slurpGroupsApi";
import { SlurpLoader } from "./HolderGroupShared";

function HowItWorksModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="pm-modal__bg" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pm-modal__close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <h3 className="pm-modal__title">How communities work</h3>
        <p className="pm-modal__lede">
          <b>A private room for every slurp token, gated by holding it.</b>
          {" "}Coordinate, post, and vote with the people actually in the
          trade.
        </p>

        <h4 className="pm-modal__h4">Joining</h4>
        <p>
          The token gate is optional. Some communities are wide open
          to any wallet, others require holding a minimum amount or
          percentage of the token to get in. The gate (or absence of
          one) is shown on the sticky note on each card. When a gate
          is set, joining is a one-time on-chain balance check signed
          by your wallet.
        </p>

        <h4 className="pm-modal__h4">What's inside</h4>
        <ul className="pm-modal__list">
          <li><b>Threads</b> with text and images, sorted by activity.</li>
          <li><b>Comments</b> and reactions on every thread.</li>
          <li><b>Polls</b> with one vote per wallet.</li>
          <li><b>Live trade feed</b> of every on-chain buy, sell, and burn for the token, streaming alongside the chat.</li>
        </ul>

        <h4 className="pm-modal__h4">Creators</h4>
        <p>
          The token deployer chooses to open the community from their
          token page. They set the join threshold (or skip it for an
          open room), banner, description, and rules. Creators can pin
          threads, hide bad content, ban troublemakers, and boot anyone
          who sold out of the token and no longer holds.
        </p>
      </div>
    </div>
  );
}

const fmtMembers = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
};
const timeAgo = (iso) => {
  if (!iso) return "no activity yet";
  const d = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - d) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const PAGE_SIZE = 20;

export default function HolderGroupsIndex() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sort, setSort] = useState("activity");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 0 when sort or search changes — otherwise you'd land
  // on a stale page that may not exist for the new query.
  useEffect(() => { setPage(0); }, [sort, debounced]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Fetch one extra row so we can tell if there's a next page without
    // a separate count query.
    slurpGroups.list({ sort, q: debounced, limit: PAGE_SIZE + 1, offset: page * PAGE_SIZE })
      .then((r) => { if (!cancelled) { setGroups(r.groups || []); setErr(null); } })
      .catch((e) => { if (!cancelled) setErr(e?.message || "Couldn't load groups"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sort, debounced, page]);

  const hasNext = groups.length > PAGE_SIZE;
  const sorted = useMemo(() => groups.slice(0, PAGE_SIZE), [groups]);
  const [howOpen, setHowOpen] = useState(false);

  return (
    <div className="pm-groups-index">
      <header className="pm-comm__hero">
        <video
          className="pm-comm__video"
          src="/images/webp/slurpcommunities.MP4"
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="pm-comm__copy">
          <div className="pm-comm__eyebrow">
            <img
              src="/images/png/slurpface (1).png"
              alt=""
              className="pm-comm__eyebrow-face"
            />
            SLURP · COMMUNITIES
          </div>
          <h1 className="pm-comm__title">
            Real holders.<br />
            <span className="pm-hero-line2">
              <span className="pm-comm__one">Real talk.</span>
              <button
                type="button"
                className="pm-hero-sticker pm-hero-sticker--desktop"
                onClick={() => setHowOpen(true)}
              >
                <Info size={14} /> See how it works
              </button>
            </span>
          </h1>
        </div>
        <div className="pm-comm__sub">
          <p>
            A forum-style, token-gated private room for every Slurp token.
            Post threads, run polls, react, track every buy and sell, and
            plan your next moves with real holders.
          </p>
          <button
            type="button"
            className="pm-hero-sticker pm-hero-sticker--mobile"
            onClick={() => setHowOpen(true)}
          >
            <Info size={14} /> See how it works
          </button>
        </div>
      </header>

      <div className="pm-groups-index__toolbar">
        <input
          type="search"
          className="pm-groups-index__search"
          placeholder="Search ticker, name, or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="pm-groups-index__sort">
          {[
            { v: "activity", label: "Activity" },
            { v: "members",  label: "Members" },
            { v: "new",      label: "Newest" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              className={`pm-groups-index__sort-btn${sort === opt.v ? " is-active" : ""}`}
              onClick={() => setSort(opt.v)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <SlurpLoader label="Loading groups" />
      ) : err ? (
        <div className="pm-groups-index__empty">{err}</div>
      ) : sorted.length === 0 ? (
        <div className="pm-groups-index__empty">
          {debounced ? `No groups match "${debounced}".` : "No holder groups yet — be the first creator to spin one up from your token page."}
        </div>
      ) : (
        <ul className="pm-groups-index__grid">
          {sorted.map((g) => (
            <li key={g.mint}>
              <Link to={`/groups/${g.mint}`} className="pm-groups-index__card">
                <div
                  className="pm-groups-index__banner"
                  style={g.banner_url ? { backgroundImage: `url(${g.banner_url})` } : undefined}
                >
                  <div className="pm-groups-index__head-row">
                    {g.token_image_url && (
                      <img src={g.token_image_url} alt="" className="pm-groups-index__token-img" />
                    )}
                    {(() => {
                      const supplyWhole = g.token_supply_raw && g.decimals != null
                        ? Number(g.token_supply_raw) / 10 ** Number(g.decimals)
                        : 0;
                      const minTokens = Number(g.min_tokens_to_join) || 0;
                      const pct = supplyWhole > 0 ? (minTokens / supplyWhole) * 100 : 0;
                      const isWhale = pct >= 1;
                      return (
                        <span className={`pm-groups-index__ticker-sticker${isWhale ? " is-whale" : ""}`}>
                          ${g.symbol} {isWhale ? "Whales" : "Holders"}
                        </span>
                      );
                    })()}
                  </div>
                  {(() => {
                    const supplyWhole = g.token_supply_raw && g.decimals != null
                      ? Number(g.token_supply_raw) / 10 ** Number(g.decimals)
                      : 0;
                    const minTokens = Number(g.min_tokens_to_join) || 0;
                    const pct = supplyWhole > 0 ? (minTokens / supplyWhole) * 100 : 0;
                    const isWhale = pct >= 1;
                    const fmt = (n) => {
                      const trim = (v) => {
                        const s = v.toFixed(1);
                        return s.endsWith(".0") ? s.slice(0, -2) : s;
                      };
                      if (n >= 1e9) return `${trim(n / 1e9)}B`;
                      if (n >= 1e6) return `${trim(n / 1e6)}M`;
                      if (n >= 1e3) return `${trim(n / 1e3)}K`;
                      return String(Math.round(n));
                    };
                    const label = minTokens <= 0
                      ? "Free to join!"
                      : pct >= 0.5
                        ? `Hold ${pct >= 10 ? pct.toFixed(0) : pct.toFixed(1)}% to join`
                        : `Hold ${fmt(minTokens)} $${g.symbol} to join`;
                    const stickerUrl = minTokens <= 0
                      ? "/images/webp/dexsticky.png"
                      : isWhale
                        ? "/images/webp/stickkycyan (1).png"
                        : "/images/webp/lpsticky.png";
                    return (
                      <div
                        className={`pm-groups-index__threshold${isWhale ? " is-whale" : ""}`}
                        style={{ backgroundImage: `url("${stickerUrl}")` }}
                      >
                        <span className="pm-groups-index__threshold-text">{label}</span>
                      </div>
                    );
                  })()}
                </div>
                <div className="pm-groups-index__body">
                  {g.description && (
                    <p className="pm-groups-index__desc">{g.description.slice(0, 110)}{g.description.length > 110 ? "…" : ""}</p>
                  )}
                  <div className="pm-groups-index__meta">
                    <span className="pm-groups-index__chip">{fmtMembers(g.member_count)} members</span>
                    <span className="pm-groups-index__chip pm-groups-index__chip--alt">
                      {Number(g.thread_count) || 0} threads
                    </span>
                    <span className="pm-groups-index__activity">· {timeAgo(g.last_activity_at)}</span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {(page > 0 || hasNext) && !loading && sorted.length > 0 && (
        <div className="pm-groups-index__pager">
          <button
            type="button"
            className="pm-groups-index__pager-btn"
            disabled={page === 0}
            onClick={() => { setPage((p) => Math.max(0, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          >
            ← prev
          </button>
          <span className="pm-groups-index__pager-page">page {page + 1}</span>
          <button
            type="button"
            className="pm-groups-index__pager-btn"
            disabled={!hasNext}
            onClick={() => { setPage((p) => p + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          >
            next →
          </button>
        </div>
      )}

      {howOpen && <HowItWorksModal onClose={() => setHowOpen(false)} />}
    </div>
  );
}
